import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';

interface UseManagementHandlersParams {
  navigate: NavigateFunction;
  saveId: string;
  setSelectedBedForPlanting: (id: string | null) => void;
  setPlantingModalVisible: (open: boolean) => void;
  clearEntitySelection: () => void;
  clearMultiSelectedBeds: () => void;
  onManagementViewBed?: (bedId: string) => void;
  onManagementViewPlanting?: (plantingId: string) => void;
  onManagementAddPlanting?: (bedId: string) => void;
  onManagementBedAction?: (bedIds: string[]) => void;
  onManagementPlantingAction?: (targets: Array<{ plantingId?: string; bedId?: string }>) => void;
}

export const useManagementHandlers = ({
  navigate,
  saveId,
  setSelectedBedForPlanting,
  setPlantingModalVisible,
  clearEntitySelection,
  clearMultiSelectedBeds,
  onManagementViewBed,
  onManagementViewPlanting,
  onManagementAddPlanting,
  onManagementBedAction,
  onManagementPlantingAction,
}: UseManagementHandlersParams) => {
  const handleManagementViewBed = useCallback((bedId: string) => {
    if (onManagementViewBed) onManagementViewBed(bedId);
    else navigate(`/saves/${saveId}/beds/${bedId}`);
  }, [navigate, onManagementViewBed, saveId]);

  const handleManagementViewPlanting = useCallback((plantingId: string) => {
    if (onManagementViewPlanting) onManagementViewPlanting(plantingId);
    else navigate(`/saves/${saveId}/plantings/${plantingId}`);
  }, [navigate, onManagementViewPlanting, saveId]);

  const handleManagementAddPlanting = useCallback((bedId: string) => {
    if (onManagementAddPlanting) onManagementAddPlanting(bedId);
    else {
      setSelectedBedForPlanting(bedId);
      setPlantingModalVisible(true);
    }
  }, [onManagementAddPlanting, setPlantingModalVisible, setSelectedBedForPlanting]);

  const handleManagementBedAction = useCallback((bedIds: string[]) => {
    onManagementBedAction?.(bedIds);
  }, [onManagementBedAction]);

  const handleManagementPlantingAction = useCallback((targets: Array<{ plantingId?: string; bedId?: string }>) => {
    onManagementPlantingAction?.(targets);
  }, [onManagementPlantingAction]);

  const handleClearManagementSelection = useCallback(() => {
    clearEntitySelection();
    clearMultiSelectedBeds();
  }, [clearEntitySelection, clearMultiSelectedBeds]);

  return {
    handleManagementViewBed,
    handleManagementViewPlanting,
    handleManagementAddPlanting,
    handleManagementBedAction,
    handleManagementPlantingAction,
    handleClearManagementSelection,
  };
};
